import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useTheme } from "next-themes";
import { InstallAppButton, IosInstallDialog } from "@/components/InstallAppButton";
import { LayoutDashboard, Package, BookOpen, Tags, Users, Receipt, FileBarChart2, Scale, Scissors, Layers, ShoppingCart, LogOut, Menu, Sun, Moon, Flame, Sparkles, PanelLeftClose, PanelLeftOpen, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/insumos", label: "Insumos", icon: Package, id: "insumos" },
  { to: "/fichas", label: "Fichas Técnicas", icon: BookOpen, id: "fichas", short: "Fichas" },
  { to: "/precificacao", label: "Precificação", icon: Tags, id: "precificacao" },
  { to: "/combos", label: "Combos", icon: Layers, id: "combos" },
  { to: "/compras", label: "Compras & Estoque", icon: ShoppingCart, id: "compras", badge: "low", short: "Compras" },
  { to: "/folha", label: "Folha de Pagamento", icon: Users, id: "folha", short: "Folha" },
  { to: "/despesas", label: "Despesas", icon: Receipt, id: "despesas" },
  { to: "/dre", label: "DRE", icon: FileBarChart2, id: "dre" },
  { to: "/ponto-equilibrio", label: "Ponto de Equilíbrio", icon: Scale, id: "ponto-equilibrio", short: "Equilíbrio" },
  { to: "/perdas", label: "Perdas de Insumos", icon: Scissors, id: "perdas", short: "Perdas" },
];

const Brand = ({ compact }) => (
  <div className="flex items-center gap-2.5 px-2 overflow-hidden">
    <div className="w-9 h-9 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30"><Flame className="w-5 h-5" /></div>
    <div className={`leading-tight whitespace-nowrap transition-all duration-300 ${compact ? "opacity-0 w-0" : "opacity-100"}`}>
      <p className="font-display font-extrabold text-base tracking-tight">Pedrada</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Gestão Financeira</p>
    </div>
  </div>
);

const ADMIN_NAV = { to: "/admin/acessos", label: "Acessos e vendas", icon: KeyRound, id: "admin-acessos", short: "Acessos" };
const navFor = (isAdmin) => (isAdmin ? [...NAV, ADMIN_NAV] : NAV);

const NavItems = ({ onClick, compact, isAdmin, lowCount = 0 }) => (
  <nav className="flex flex-col gap-1">
    {navFor(isAdmin).map((n) => (
      <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onClick} data-testid={`nav-${n.id}`} title={compact ? n.label : undefined}
        className={({ isActive }) => `relative flex items-center gap-3 rounded-xl text-sm font-medium transition-colors ${compact ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
        <n.icon className="w-[18px] h-[18px] shrink-0" />
        <span className={`whitespace-nowrap overflow-hidden transition-all duration-300 flex-1 ${compact ? "w-0 opacity-0" : "opacity-100"}`}>{n.label}</span>
        {n.badge === "low" && lowCount > 0 && <span data-testid="nav-compras-badge" className={`num min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center ${compact ? "absolute -top-0.5 right-1" : ""}`}>{lowCount}</span>}
      </NavLink>
    ))}
  </nav>
);

const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const dark = theme !== "light";
  return (
    <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={() => setTheme(dark ? "light" : "dark")} aria-label="Alternar tema">
      {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </Button>
  );
};

// Barra inferior (mobile/tablet): todos os módulos em uma linha com scroll horizontal; o item ativo é centralizado automaticamente.
const BottomNav = ({ isAdmin, lowCount = 0 }) => {
  const ref = useRef(null);
  const loc = useLocation();
  useEffect(() => {
    const box = ref.current;
    const el = box?.querySelector('[aria-current="page"]');
    if (!box || !el) return;
    box.scrollTo({ left: el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
  }, [loc.pathname]);
  return (
    <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border safe-bottom">
      <div ref={ref} className="h-16 flex items-stretch bottom-nav-scroll no-scrollbar" data-testid="bottom-nav-scroll">
        {navFor(isAdmin).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-nav-${n.id}`} aria-label={n.label}
            className={({ isActive }) => `relative snap-center shrink-0 grow basis-[4.5rem] min-w-[4.5rem] px-2 flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            {({ isActive }) => (<>
              <span className={`relative flex items-center justify-center w-11 h-7 rounded-full transition-colors ${isActive ? "bg-primary/15" : ""}`}>
                <n.icon className="w-5 h-5" />
                {n.badge === "low" && lowCount > 0 && <span data-testid="bottom-nav-compras-badge" className="num absolute -top-1 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{lowCount}</span>}
              </span>
              <span className="whitespace-nowrap leading-none">{n.short || n.label}</span>
            </>)}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { data: shop, reload: reloadShop } = useApi("/shopping");
  const lowCount = shop?.low_count || 0;
  const loc = useLocation();
  useEffect(() => { reloadShop(); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = () => reloadShop(); window.addEventListener("pb:stock-changed", h); return () => window.removeEventListener("pb:stock-changed", h); }, [reloadShop]);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pb_sidebar") === "closed");
  const toggleSidebar = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem("pb_sidebar", v ? "closed" : "open"); };
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-app flex">
      <aside data-testid="sidebar" data-state={collapsed ? "collapsed" : "open"} className={`hidden lg:flex flex-col border-r border-border bg-card/60 sticky top-0 h-app overflow-hidden transition-[width,padding] duration-300 ease-in-out ${collapsed ? "w-[76px] p-3" : "w-64 p-4"}`}>
        <div className="flex items-center justify-between gap-2 shrink-0" data-testid="sidebar-header">
          <Brand compact={collapsed} />
          <Button variant="ghost" size="icon" className="shrink-0" data-testid="sidebar-toggle" onClick={toggleSidebar} aria-label={collapsed ? "Abrir menu" : "Fechar menu"}>
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden nav-scroll my-6 -mx-1 px-1" data-testid="sidebar-nav-scroll">
          <NavItems compact={collapsed} isAdmin={user?.is_admin} lowCount={lowCount} />
        </div>
        <div className="shrink-0 space-y-2" data-testid="sidebar-footer">
          <InstallAppButton collapsed={collapsed} />
          <button data-testid="nav-onboarding" onClick={() => nav("/onboarding")} title="Rever tutorial guiado" className={`w-full flex items-center gap-2 text-xs py-2 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 transition-colors ${collapsed ? "justify-center px-0" : "px-3"}`}>
            <Sparkles className="w-3.5 h-3.5 shrink-0" /><span className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${collapsed ? "w-0 opacity-0" : "opacity-100"}`}>Rever tutorial guiado</span>
          </button>
          <div className={`flex items-center pt-2 border-t border-border ${collapsed ? "flex-col gap-1" : "justify-between px-2"}`}>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.business_name || user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            )}
            <div className={`flex ${collapsed ? "flex-col" : ""}`}><ThemeToggle /><Button variant="ghost" size="icon" data-testid="logout-btn" onClick={doLogout} aria-label="Sair"><LogOut className="w-4 h-4" /></Button></div>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 glass border-b border-border safe-top safe-x">
          <div className="h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="w-5 h-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-72 p-4 sheet-safe overflow-y-auto">
                <div className="mb-6"><Brand /></div>
                <NavItems onClick={() => setOpen(false)} isAdmin={user?.is_admin} lowCount={lowCount} />
                <div className="mt-6 border-t border-border pt-4 space-y-2">
                  <InstallAppButton variant="mobile" onDone={() => setOpen(false)} onIos={() => { setOpen(false); setIosHelp(true); }} />
                  <Button variant="outline" className="w-full justify-start" data-testid="mobile-onboarding-btn" onClick={() => { setOpen(false); nav("/onboarding"); }}><Sparkles className="w-4 h-4 mr-2" />Tutorial guiado</Button>
                  <Button variant="ghost" className="w-full justify-start" data-testid="mobile-logout-btn" onClick={doLogout}><LogOut className="w-4 h-4 mr-2" />Sair</Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 pt-4 sm:pt-6 lg:pt-10 pb-nav lg:pb-10 main-x w-full max-w-[1600px] mx-auto transition-[max-width] duration-300"><Outlet /></main>
        <IosInstallDialog open={iosHelp} onOpenChange={setIosHelp} />

        <BottomNav isAdmin={user?.is_admin} lowCount={lowCount} />
      </div>
    </div>
  );
}
